import { type RefObject, useCallback, useMemo, useRef, useState } from "react";
import { type TaskCreateBody, useCreateTask } from "@api/task.api";
import { type Task, TaskGroupBy } from "@common/types/task";
import type {
    BoardAddPlacement,
    BoardAddTarget,
    BoardColumnDef,
    BoardLock,
    BoardPlacement,
} from "@design-system/Board/Board.types";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    type TaskPlaceholderFields,
    type TaskPlaceholderFilters,
    toTaskCreateBody,
} from "../taskPlaceholderFields";
import {
    buildDraftTask,
    compareDraftTo,
    getDraftTaskFields,
    TASK_BOARD_DRAFT_ID,
} from "./taskBoardDraft";
import type { TaskBoardPin } from "./taskBoardPins";
import { findTask, getTaskPlaceValues } from "./taskBoardPlace";
import type { TaskBoardSort } from "./taskBoardSort";
import type { TaskBoardTask } from "../types";
import { useTaskBoardDraftFocus } from "./useTaskBoardDraftFocus";
import type { TaskBoardModel } from "./useTaskBoardModel";

const COLUMN_ADD: readonly BoardAddPlacement[] = [
    "header",
    "between",
    "footer",
];
const LANE_ADD: readonly BoardAddPlacement[] = ["between", "footer"];
const DRAFT_LOCK: BoardLock = {};

interface TaskBoardDraft {
    placement: BoardPlacement;
    fields: TaskPlaceholderFields;
    task: Task;
    /** Where the created task stays: beside the draft's neighbours. */
    pin: TaskBoardPin;
}

/** What a create's response hands off to: the pin, when it copied a value, and the focus. */
interface TaskCreateHandOff {
    pin: TaskBoardPin | null;
    focusCreated: ((taskId: string) => void) | null;
}

interface UseTaskBoardCreateArgs {
    tasks: readonly TaskBoardTask[];
    model: TaskBoardModel;
    /** The board sort: a sort that takes placed cards gives the new task its neighbour's value. */
    sort: TaskBoardSort;
    filters: TaskPlaceholderFilters;
    canCreateTasks: boolean;
    onCreated: (taskId: string) => void;
    /** The board's wrapper, where focus goes back to after a draft. */
    rootRef: RefObject<HTMLElement | null>;
}

const getAddLabel = (column: BoardColumnDef) =>
    `Create task in ${column.title}`;

/**
 * Creating from a column's `+`: the header `+` (without lanes), the gap `+`
 * between cards and the footer `+` open a draft card with a title field
 * where they sit, in any sort. The task takes the column's and lane's values
 * and, under a sort that takes placed cards, the sort field's value of the
 * card above (else below), and stays where its draft opened on this board
 * (see `TaskBoardPin`); under other sorts it lands where the sort puts it.
 * Not offered by close stage, which List can't create into either.
 */
export const useTaskBoardCreate = ({
    tasks,
    model,
    sort,
    filters,
    canCreateTasks,
    onCreated,
    rootRef,
}: UseTaskBoardCreateArgs) => {
    const [draft, setDraft] = useState<TaskBoardDraft | null>(null);
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const toaster = useToaster();
    const handOffsRef = useRef(
        new WeakMap<TaskCreateBody, TaskCreateHandOff>(),
    );
    const { pinTask, compareTasks: compareBoardTasks } = model;
    const { mutate: createTask } = useCreateTask({
        onSuccess: (created, body) => {
            const handOff = handOffsRef.current.get(body);
            if (handOff?.pin) {
                pinTask(created.id, handOff.pin);
            }
            onCreated(created.id);
            handOff?.focusCreated?.(created.id);
        },
        onError: () => toaster.error("Failed to create task"),
    });
    const { rememberOrigin, returnFocus, getCreatedFocus } =
        useTaskBoardDraftFocus({ rootRef, tasks });
    const isCreateShown =
        canCreateTasks && model.groupBy !== TaskGroupBy.CLOSE_STAGE;

    const columns = useMemo(() => {
        if (!isCreateShown) {
            return model.columns;
        }
        const addPlacements = model.lanes === undefined ? COLUMN_ADD : LANE_ADD;
        return model.columns.map((column) => ({ ...column, addPlacements }));
    }, [isCreateShown, model.columns, model.lanes]);

    const onAddItem = useCallback(
        ({ placement, beforeItemId, afterItemId }: BoardAddTarget) => {
            if (refuseIfViewOnly()) {
                return;
            }
            const neighbours = {
                before: findTask(tasks, beforeItemId),
                after: findTask(tasks, afterItemId),
            };
            const placeValues = getTaskPlaceValues({
                sort,
                axes: model.axes,
                ...neighbours,
            });
            const fields = {
                ...getDraftTaskFields({
                    placement,
                    groupBy: model.groupBy,
                    laneBy: model.laneBy,
                    sampleTask: model.tasksByColumnId.get(
                        placement.columnId,
                    )?.[0],
                    filters,
                }),
                ...placeValues,
            };
            rememberOrigin(placement);
            setDraft({
                placement,
                fields,
                task: buildDraftTask(fields),
                pin: { neighbours, sort, placeValues },
            });
        },
        [refuseIfViewOnly, tasks, model, sort, filters, rememberOrigin],
    );

    const items = useMemo<readonly TaskBoardTask[]>(
        () => (draft === null ? tasks : [draft.task, ...tasks]),
        [draft, tasks],
    );

    const getPlacements = useCallback(
        (task: Task) =>
            draft !== null && task.id === TASK_BOARD_DRAFT_ID
                ? [draft.placement]
                : model.getPlacements(task),
        [draft, model],
    );

    const compareTasks = useCallback(
        (first: Task, second: Task) => {
            if (draft !== null && first.id === TASK_BOARD_DRAFT_ID) {
                return compareDraftTo({
                    neighbours: draft.pin.neighbours,
                    task: second,
                    compareTasks: compareBoardTasks,
                });
            }
            if (draft !== null && second.id === TASK_BOARD_DRAFT_ID) {
                return -compareDraftTo({
                    neighbours: draft.pin.neighbours,
                    task: first,
                    compareTasks: compareBoardTasks,
                });
            }
            return compareBoardTasks(first, second);
        },
        [draft, compareBoardTasks],
    );

    const getDraftLock = useCallback(
        (task: Task) => (task.id === TASK_BOARD_DRAFT_ID ? DRAFT_LOCK : null),
        [],
    );

    const submitDraft = useCallback(
        (title: string) => {
            if (draft === null) {
                return;
            }
            const { fields, pin } = draft;
            const body = toTaskCreateBody({ title, fields });
            handOffsRef.current.set(body, {
                pin: Object.keys(pin.placeValues).length > 0 ? pin : null,
                focusCreated: getCreatedFocus(),
            });
            createTask(body);
        },
        [draft, getCreatedFocus, createTask],
    );

    const endDraft = useCallback(() => setDraft(null), []);

    return {
        columns,
        items,
        getPlacements,
        compareTasks,
        getDraftLock,
        addProps: isCreateShown
            ? { onAddItem, getAddLabel, canAddBetween: true }
            : {},
        submitDraft,
        endDraft,
        returnDraftFocus: returnFocus,
    };
};
